import { REPORT_REASON_LABELS, REPORT_REASONS, type ReportReason } from "@hexling/core";
import { Button, Dialog, SelectField, StatusMessage, TextAreaField, TextField } from "@hexling/ui";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLists, useServices } from "../data/AppData";
import type { Ref } from "../data/user";
import { errorMessage } from "../lib/api";
import { Icon } from "./Icon";

/** Bookmark, add to list, write a note, and report: the actions on every library item. */
export function ItemActions({ item }: { item: Ref & { name: string } }) {
  const { user } = useServices();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ref: Ref = { category: item.category, slug: item.slug };
  const bookmarked = useQuery({
    queryKey: ["user", "bookmarked", ref.category, ref.slug],
    queryFn: () => user.isBookmarked(ref),
  });
  const [dialog, setDialog] = useState<"list" | "report" | null>(null);
  const [status, setStatus] = useState("");

  const toggle = async () => {
    const on = await user.toggleBookmark(ref);
    setStatus(on ? `Bookmarked ${item.name}` : `Removed bookmark`);
    await qc.invalidateQueries({ queryKey: ["user"] });
  };

  const newNote = () =>
    navigate({
      to: "/grimoire/notes/$id",
      params: { id: "new" },
      search: { ref: `${ref.category}:${ref.slug}` },
    });

  return (
    <div className="item-actions">
      <div className="item-actions__row">
        <Button
          variant="secondary"
          size="small"
          onClick={toggle}
          aria-pressed={bookmarked.data ?? false}
        >
          <Icon name="bookmark" size={18} filled={bookmarked.data} />
          {bookmarked.data ? "Bookmarked" : "Bookmark"}
        </Button>
        <Button variant="secondary" size="small" onClick={() => setDialog("list")}>
          <Icon name="list" size={18} />
          Add to list
        </Button>
        <Button variant="secondary" size="small" onClick={newNote}>
          <Icon name="note" size={18} />
          Write a note
        </Button>
        <Button variant="ghost" size="small" onClick={() => setDialog("report")}>
          <Icon name="flag" size={18} />
          Report a problem
        </Button>
      </div>
      <StatusMessage>{status}</StatusMessage>
      <AddToListDialog
        open={dialog === "list"}
        item={item}
        onClose={() => setDialog(null)}
        onDone={setStatus}
      />
      <ReportDialog
        open={dialog === "report"}
        item={item}
        onClose={() => setDialog(null)}
        onDone={setStatus}
      />
    </div>
  );
}

function AddToListDialog({
  open,
  item,
  onClose,
  onDone,
}: {
  open: boolean;
  item: Ref & { name: string };
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const { user } = useServices();
  const lists = useLists().data ?? [];
  const [newName, setNewName] = useState("");

  const add = async (listId: string, listName: string) => {
    await user.addToList(listId, { category: item.category, slug: item.slug });
    onDone(`Added ${item.name} to ${listName}`);
    onClose();
  };

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    const list = await user.saveList({ name, description: "", icon: "" });
    setNewName("");
    await add(list.id, list.name);
  };

  return (
    <Dialog open={open} title={`Add ${item.name} to a list`} onClose={onClose}>
      {lists.length > 0 && (
        <ul className="plain-list">
          {lists.map((l) => (
            <li key={l.id}>
              <Button variant="secondary" onClick={() => add(l.id, l.name)}>
                {l.icon ? `${l.icon} ` : ""}
                {l.name}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <TextField
          label="New list"
          placeholder="Sleep spells, garden plans…"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          maxLength={100}
        />
        <Button type="submit" disabled={!newName.trim()}>
          Create list and add
        </Button>
      </form>
    </Dialog>
  );
}

/** Lets anyone flag incorrect or unsafe content. Staff get an email and an admin queue entry. */
export function ReportDialog({
  open,
  item,
  onClose,
  onDone,
}: {
  open: boolean;
  item: Ref & { name: string };
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const { api } = useServices();
  const [reason, setReason] = useState<ReportReason>("incorrect_info");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.send("POST", "/api/reports", {
        category: item.category,
        slug: item.slug,
        reason,
        message,
      });
      setMessage("");
      onDone("Thanks. Your report was sent to the Hexling team.");
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title={`Report a problem with ${item.name}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} busy={busy} disabled={message.trim().length < 10}>
            Send report
          </Button>
        </>
      }
    >
      <p className="hx-muted">Spotted something wrong or unsafe? Tell us and we'll fix it.</p>
      <SelectField
        label="What kind of problem?"
        value={reason}
        onChange={(e) => setReason(e.target.value as ReportReason)}
        options={REPORT_REASONS.map((r) => ({ value: r, label: REPORT_REASON_LABELS[r] }))}
      />
      <TextAreaField
        label="Details"
        hint="At least 10 characters. Sources help us fix it faster."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        maxLength={2000}
        error={error}
      />
    </Dialog>
  );
}
